import { AttendanceType } from '@fingertip/creator-proto/gen/fingertip/common/enum/v1/attendance_type_pb'
import {
  EmojiSadFilledIcon,
  PlusLargeIcon,
  TrashCanSimpleIcon,
} from '@fingertip/icons'
import { observer } from 'mobx-react-lite'
import { useCallback, useContext } from 'react'
import { useFieldArray } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { BookerBookingFormLocationSelector } from '@/components/booking/booker/components/booker-booking-form-location-selector'
import { PhoneField } from '@/components/fields/phone-field'
import { BookingSelection } from '@/lib/hooks/use-booking-selection'
import { RootStoreContext } from '@/lib/stores/root-store'

import { InputField } from '../../../fields/input-field'
import { TextareaField } from '../../../fields/textarea-field'
import { Button } from '../../../ui/button'

type Props = {
  control: any
  watch: any
  readOnly?: boolean
  bookingSelection: BookingSelection
  waitlisted?: boolean
}

export const BookerBookingForm = observer(
  ({ bookingSelection, control, readOnly, watch, waitlisted }: Props) => {
    const { t } = useTranslation()
    const {
      bookerStore: { selectedLocation },
    } = useContext(RootStoreContext)
    const { fields, append, remove } = useFieldArray({
      control,
      name: 'guests',
    })

    const handleDeleteClick = useCallback(
      (index: number) => {
        remove(index)
      },
      [remove],
    )

    const handleAddClick = useCallback(() => {
      append('')
    }, [append])

    return (
      <>
        {waitlisted && (
          <div className="flex flex-col gap-4 mb-4">
            <div className="flex items-center border text-warning-foreground border-warning-foreground bg-warning-background rounded-lg p-3 gap-4">
              <EmojiSadFilledIcon className="size-10 text-warning"></EmojiSadFilledIcon>
              <div>
                <h3 className="font-medium text-base">This event is full</h3>
                <p className="text-xs">
                  But if you're interested, you can join the waitlist by
                  completing the form below.
                </p>
              </div>
            </div>

            <div>
              <h3 className="font-medium text-base">Join the waitlist</h3>
              <p className="text-muted-foreground text-sm">
                You will be notified via email if a spot opens up
              </p>
            </div>
          </div>
        )}

        <InputField
          control={control}
          name="name"
          label={t('full_name')}
          placeholder={t('e_g_john_smith')}
          disabled={readOnly}
        />

        <InputField
          control={control}
          name="email"
          label={t('email_address')}
          placeholder={t('e_g_hello_gmail_com')}
          disabled={readOnly}
        />

        {!waitlisted && (
          <PhoneField
            control={control}
            name="phone"
            label={
              selectedLocation?.type === 'ATTENDEE_PHONE'
                ? t('phone_number')
                : t('phone_number_optional')
            }
            placeholder="E.g. +61 412 345 678"
            disabled={readOnly}
          />
        )}

        {!waitlisted && (
          <BookerBookingFormLocationSelector
            watch={watch}
            control={control}
            readOnly={readOnly}
            bookingLocation={bookingSelection.bookingItemsLocation}
          />
        )}

        {!waitlisted && (
          <TextareaField
            control={control}
            name="notes"
            label={t('notes_optional')}
            placeholder={t(
              'please_share_anything_that_will_help_prepare_for_the_meeting',
            )}
            disabled={readOnly}
            minRows={2}
          />
        )}

        {/* Additional Guests */}
        {!waitlisted &&
          bookingSelection.bookingItemsAttendanceType ===
            AttendanceType.MEETING && (
            <>
              {fields.map((field, index) => {
                return (
                  <div key={field.id} className="flex">
                    <InputField
                      control={control}
                      name={`guests.${index}`}
                      label={`Guest ${fields.length > 1 ? index + 1 : ''}`}
                      placeholder={t('enter_guest_email')}
                      disabled={readOnly}
                    />

                    <Button
                      type="button"
                      variant="destructiveSecondary"
                      onClick={() => handleDeleteClick(index)}
                      className="mb-2 ml-2 mt-8"
                      disabled={readOnly}
                    >
                      <TrashCanSimpleIcon className="size-4" />
                    </Button>
                  </div>
                )
              })}

              <div className="mb-4">
                <Button
                  type="button"
                  onClick={handleAddClick}
                  size="sm"
                  variant="secondary"
                  disabled={readOnly}
                >
                  <PlusLargeIcon className="size-4 mr-2" />
                  {t('add_guest')}
                </Button>
              </div>
            </>
          )}
      </>
    )
  },
)
